import React, { useState, useEffect } from "react";
import Alert from "../HtmlElements/Alert";
import {
  otp,
  resendOtp,
  fetchUserDetails,
  removeCheckoutClientInfo,
} from "../../services/authService";
import {
  buildAuthenticatedReportUrl,
  getUserStatsObject,
  recordActivity,
} from "../../services/commonService";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import "./Login.css";

const OTPVerification = ({
  tempApiToken,
  email,
  setTempApiToken,
  setShowOtpScreen,
  currentUrl,
  pageParsedData,
  handleLogout,
}) => {
  const { setUser } = useUser();
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtpValue(value);
    if (error) setError("");
  };

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOtp = async () => {
    if (resending || resendTimer > 0) return;

    setResending(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await resendOtp(tempApiToken, "default-device");

      setSuccessMessage(response.result?.message || "A new OTP has been sent!");

      setResendTimer(30);

      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err) {
      const errorMessage =
        err.result?.message || "Failed to resend OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (otpValue.length < 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }

    setLoading(true);
    try {
      const isOtpVerified = await otp(tempApiToken, otpValue, "default-device");

      if (isOtpVerified === true) {
        setSuccessMessage("OTP verified successfully!");

        const userStat = getUserStatsObject(
          "Login",
          "login",
          "Login successful",
          currentUrl
        );
        userStat.url_details = pageParsedData;
        recordActivity(userStat);

        localStorage.setItem("loginFailedCount", 0);
        let userInfo = await fetchUserDetails();
        setUser(userInfo);

        removeCheckoutClientInfo();
        setTempApiToken(null);
        setShowOtpScreen(false);

        const growthiq_role = userInfo?.growth_iq_role_name;

        if (userInfo?.growth_iq === 1) {
          if (
            growthiq_role === "ROLE_SUBSCRIBER_EMPLOYEE" ||
            growthiq_role === "ROLE_SUBSCRIBER_ADMIN"
          ) {
            if (userInfo?.has_ask_growth_access === 1) {
              navigate("/watchboard", { replace: true });
            } else if (userInfo?.has_comp_iq_access === 1) {
              navigate("/clients_competitors", { replace: true });
            } else if (userInfo?.salesplay === 1) {
              navigate("/customers", { replace: true });
            } else {
              navigate("/weekly_report", { replace: true });
            }
          } else {
            navigate("/client_industry", { replace: true });
          }
        } else {
          window.location.replace(
            buildAuthenticatedReportUrl("https://www.mnmks.com")
          );
        }
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (err) {
      setError(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="otp_wrapper">
      <div className="otp_block_start">
        <div className="otp_block">
          {successMessage && <Alert type="success" message={successMessage} />}
          {error && <Alert type="danger" message={error} />}

          <h5 className="otp_block_head">
            Please enter the One-Time-
            <br />
            Password to verify the account
          </h5>

          <p className="otp_block_para">
            A One-Time Password has been sent to
            <br />
            <span
              className="email_user"
              style={{ color: "#007bff", fontWeight: "bold" }}
            >
              {email}
            </span>
          </p>

          <div className="otp_form_block">
            <form className="otp_form" onSubmit={handleOtpSubmit}>
              <div className="mb-3 otp_form_input">
                <input
                  className="text-center form-control rounded"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otpValue}
                  onChange={handleOtpChange}
                  autoComplete="one-time-code"
                />
                <span
                  className="resend_span"
                  onClick={handleResendOtp}
                  style={{
                    cursor:
                      resending || resendTimer > 0 ? "not-allowed" : "pointer",
                    opacity: resending || resendTimer > 0 ? 0.6 : 1,
                    textDecoration: "underline",
                    display: "inline-block",
                    marginTop: "5px",
                  }}
                >
                  {resending ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>Sending...
                    </>
                  ) : resendTimer > 0 ? (
                    `Resend OTP in ${resendTimer}s`
                  ) : (
                    "Resend One-Time Password"
                  )}
                </span>
              </div>

              <div className="mt-4 otp_form_submit mb-4">
                <button
                  id="validateBtn"
                  type="submit"
                  className="btn btn-danger px-4 validate w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>{" "}
                      Verifying...
                    </>
                  ) : (
                    "Validate"
                  )}
                </button>
              </div>
            </form>

            <div className="entered_wrong_mail_block">
              {/* <p className="entered_mail_para">Entered a wrong email id?</p> */}
              <p className="incase_para">
                In case if you not have received the OTP, Request you to check
                OTP email in spam folder or get in touch with us
                <br />
                <a
                  href="mailto:mnmkssupport@marketsandmarkets.com"
                  className="incase_para_mail"
                >
                  mnmkssupport@marketsandmarkets.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
